import { useCallback } from 'react';
import { timeToPercent } from '../utils/timelineUtils';

export const usePlaybackHandlers = (state, setState) => {
  const {
    isPlaying,
    playbackInitialIndicatorTime,
    setIsPlaying,
    setPlaybackStartTime,
    setPlaybackInitialIndicatorTime,
    setCurrentTimeIndicator,
    timelineEffects,
    effectEndTime,
    effectStartTime,
  } = state;

  const {
    setIsPlaying: setIsPlayingState,
    setPlaybackStartTime: setPlaybackStartTimeState,
    setPlaybackInitialIndicatorTime: setPlaybackInitialIndicatorTimeState,
    setCurrentTimeIndicator: setCurrentTimeIndicatorState,
  } = setState;

  // Play/Pause functionality
  const handlePlayPause = useCallback(() => {
    if (isPlaying) {
      // Pause – simply stop progressing time but keep the current indicator in state
      setIsPlayingState(false);
      setPlaybackStartTimeState(null);
    } else {
      // Play or resume
      setIsPlayingState(true);
      const now = performance.now();
      const offset = playbackInitialIndicatorTime || 0;
      setPlaybackStartTimeState(now - offset);

      // only reset indicator if we're starting from the very beginning
      if (offset === 0) {
        setPlaybackInitialIndicatorTimeState(0); // reset stored offset
        setCurrentTimeIndicatorState({
          position: 0,
          time: 0,
          visible: true,
        });
      }
    }
  }, [
    isPlaying,
    playbackInitialIndicatorTime,
    setIsPlayingState,
    setPlaybackStartTimeState,
    setPlaybackInitialIndicatorTimeState,
    setCurrentTimeIndicatorState,
  ]);

  // Go to start
  const handleGoToStart = useCallback(() => {
    setIsPlayingState(false);
    setPlaybackStartTimeState(null);
    const startPosition = timeToPercent(0, effectStartTime, effectEndTime);
    setCurrentTimeIndicatorState({
      position: startPosition,
      time: 0,
      visible: true,
    });
  }, [
    effectStartTime,
    effectEndTime,
    setIsPlayingState,
    setPlaybackStartTimeState,
    setCurrentTimeIndicatorState,
  ]);

  // Go to end
  const handleGoToEnd = useCallback(() => {
    setIsPlayingState(false);
    setPlaybackStartTimeState(null);

    // Find the maximum end time of all effects
    const maxEffectEndTime =
      timelineEffects.length > 0
        ? Math.max(...timelineEffects.map((effect) => effect.endTime))
        : effectEndTime;

    const endPosition = timeToPercent(
      maxEffectEndTime,
      effectStartTime,
      effectEndTime
    );
    setCurrentTimeIndicatorState({
      position: endPosition,
      time: maxEffectEndTime,
      visible: true,
    });
  }, [
    timelineEffects,
    effectEndTime,
    effectStartTime,
    setIsPlayingState,
    setPlaybackStartTimeState,
    setCurrentTimeIndicatorState,
  ]);

  return {
    handlePlayPause,
    handleGoToStart,
    handleGoToEnd,
  };
};
